/**
 * usePolicy Hook - Policy-based permission system
 * Uses Better Auth policy system from backend
 */

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/components/AuthProvider';

export type Scope = 'ALL' | 'OWN' | 'NONE';

export interface PolicyResource {
  actions: Record<string, Scope>;
}

export interface Policy {
  resources?: Record<string, PolicyResource>;
  ui?: {
    menu?: Record<string, boolean>;
    elements?: {
      widget?: Record<string, boolean>;
      column?: Record<string, boolean>;
      action?: Record<string, boolean>;
      tab?: Record<string, boolean>;
      input?: Record<string, boolean>;
      select?: Record<string, boolean>;
    } | Record<string, boolean>; // Support both old and new format
  };
  workflow?: Record<string, any>;
}

export function usePolicy() {
  const { user } = useAuth();
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Default empty policy structure
    const defaultPolicy: Policy = {
      resources: {},
      ui: {
        menu: {},
        elements: {
          widget: {},
          column: {},
          action: {},
          tab: {},
          input: {},
          select: {},
        },
      },
      workflow: {},
    };

    if (!user) {
      setPolicy(defaultPolicy);
      setLoading(false);
      setError(null);
      return;
    }

    // Get policy from user object
    const userPolicy = (user as any)?.policy;
    
    if (userPolicy) {
      try {
        // Normalize policy structure - ensure all fields exist
        const normalizedPolicy: Policy = {
          resources: userPolicy.resources || {},
          ui: {
            menu: userPolicy.ui?.menu || {},
            elements: userPolicy.ui?.elements || {
              widget: {},
              column: {},
              action: {},
              tab: {},
              input: {},
              select: {},
            },
          },
          workflow: userPolicy.workflow || {},
        };
        setPolicy(normalizedPolicy);
        setLoading(false);
        setError(null);
      } catch (err: any) {
        console.error('[usePolicy] Error normalizing policy:', err);
        setError(err.message);
        setLoading(false);
        // Use default policy on error
        setPolicy(defaultPolicy);
      }
    } else {
      // No policy in user object - use default policy immediately
      setPolicy(defaultPolicy);
      setLoading(false);
    }
  }, [user]);

  const can = useMemo(() => {
    return (resource: string, action: string, context?: { resourceId?: number; authorId?: number; ownerId?: number }): boolean => {
      // Check if user is Super Admin
      const userRole = (user as any)?.role;
      const userRoles = (user as any)?.roles || [];
      const roleLower = userRole?.toLowerCase() || '';
      
      const isSuperAdmin = 
        userRole === 'SUPERADMIN' || 
        userRole === 'ADMIN' || 
        userRole === 'Super Admin' || 
        userRole === 'superadmin' ||
        userRole === 'admin' ||
        userRole === 'super_admin' ||
        roleLower === 'super admin' ||
        roleLower.includes('super admin') ||
        userRoles.some((r: any) => {
          const roleName = r.name?.toLowerCase() || '';
          const roleSlug = r.slug?.toLowerCase() || '';
          return (
            roleName.includes('super admin') || 
            roleSlug === 'super-admin' ||
            roleSlug === 'superadmin' ||
            r.name === 'Super Admin' ||
            r.name === 'SUPERADMIN' ||
            r.name === 'Admin' ||
            r.slug === 'admin'
          );
        });

      if (isSuperAdmin) {
        return true; // Super Admin can do everything
      }

      if (!policy || !policy.resources || !policy.resources[resource]) {
        return false;
      }

      // Check for wildcard permission
      const wildcardScope = policy.resources[resource].actions['*'];
      if (wildcardScope && wildcardScope !== 'NONE') {
        return true;
      }

      // Alias: update↔edit, read↔view, resolve↔moderate (seed/UI farklı anahtar kullanabiliyor)
      const aliases: Record<string, string[]> = {
        update: ['update', 'edit', 'resolve'],
        edit: ['edit', 'update'],
        read: ['read', 'view'],
        view: ['view', 'read'],
        create: ['create', 'upload'],
        upload: ['upload', 'create'],
        resolve: ['resolve', 'update', 'edit', 'moderate'],
        moderate: ['moderate', 'resolve', 'manage'],
      };
      const keys = aliases[action] || [action];
      let scope: Scope = 'NONE';
      for (const key of keys) {
        const s = policy.resources[resource].actions[key];
        if (s && s !== 'NONE') {
          scope = s;
          break;
        }
        if (s === 'NONE') scope = 'NONE';
      }

      if (scope === 'NONE') {
        return false;
      }

      // OWN scope: user can only access their own resources
      if (scope === 'OWN') {
        // For menu visibility (read action without context), allow if OWN scope exists
        // The actual filtering will happen in the backend/list endpoint
        if (!context || (!context.authorId && !context.ownerId && !context.resourceId)) {
          // No context provided - this is likely a menu visibility check
          // Allow menu to show, backend will filter the content
          return true;
        }
        
        const userId = (user as any)?.id;
        // Check authorId first (for new articles with authorId field)
        if (context?.authorId && context.authorId === userId) {
          return true;
        }
        // Check ownerId (generic owner field - can be authorId or editorId)
        if (context?.ownerId && context.ownerId === userId) {
          return true;
        }
        // If context provided but doesn't match, deny access
        return false;
      }

      // ALL scope: allow
      return true;
    };
  }, [policy, user]);

  const scope = useMemo(() => {
    return (resource: string, action: string): Scope => {
      const userRole = (user as any)?.role;
      const userRoles = (user as any)?.roles || [];
      const roleLower = userRole?.toLowerCase() || '';
      const isSuperAdmin =
        userRole === 'SUPERADMIN' ||
        userRole === 'ADMIN' ||
        userRole === 'Super Admin' ||
        userRole === 'superadmin' ||
        userRole === 'admin' ||
        userRole === 'super_admin' ||
        roleLower === 'super admin' ||
        roleLower.includes('super admin') ||
        userRoles.some((r: any) => {
          const roleName = r.name?.toLowerCase() || '';
          const roleSlug = r.slug?.toLowerCase() || '';
          return (
            roleName.includes('super admin') ||
            roleSlug === 'super-admin' ||
            roleSlug === 'superadmin' ||
            r.name === 'Super Admin' ||
            r.name === 'SUPERADMIN' ||
            r.name === 'Admin' ||
            r.slug === 'admin'
          );
        });
      if (isSuperAdmin) return 'ALL';

      if (!policy?.resources?.[resource]) {
        return 'NONE';
      }

      const aliases: Record<string, string[]> = {
        update: ['update', 'edit', 'resolve'],
        edit: ['edit', 'update'],
        read: ['read', 'view'],
        view: ['view', 'read'],
        create: ['create', 'upload'],
        upload: ['upload', 'create'],
        resolve: ['resolve', 'update', 'edit', 'moderate'],
        moderate: ['moderate', 'resolve', 'manage'],
      };
      for (const key of aliases[action] || [action]) {
        const s = policy.resources[resource].actions[key];
        if (s) return s;
      }
      return 'NONE';
    };
  }, [policy, user]);

  const canSeeMenu = useMemo(() => {
    return (menuKey: string): boolean => {
      // Check if user is Super Admin
      const userRole = (user as any)?.role;
      const userRoles = (user as any)?.roles || [];
      const roleLower = userRole?.toLowerCase() || '';
      
      const isSuperAdmin = 
        userRole === 'SUPERADMIN' || 
        userRole === 'ADMIN' || 
        userRole === 'Super Admin' || 
        userRole === 'superadmin' ||
        userRole === 'admin' ||
        userRole === 'super_admin' ||
        roleLower === 'super admin' ||
        roleLower.includes('super admin') ||
        roleLower === 'admin' ||
        userRoles.some((r: any) => {
          const roleName = r.name?.toLowerCase() || '';
          const roleSlug = r.slug?.toLowerCase() || '';
          return (
            roleName.includes('super admin') || 
            roleSlug === 'super-admin' ||
            roleSlug === 'superadmin' ||
            r.name === 'Super Admin' ||
            r.name === 'SUPERADMIN' ||
            r.name === 'Admin' ||
            r.slug === 'admin'
          );
        });

      if (isSuperAdmin) {
        return true; // Super Admin can see all menus
      }

      const menu = policy?.ui?.menu as Record<string, unknown> | undefined;
      if (!menu || typeof menu !== 'object') {
        return false;
      }
      const value = menu[menuKey];
      return value === true || value === 'true' || value === 1 || value === '1';
    };
  }, [policy, user]);

  const canSeeElement = useMemo(() => {
    return (
      category: 'widget' | 'column' | 'action' | 'tab' | 'input' | 'select',
      elementKey: string
    ): boolean => {
      // Check if user is Super Admin
      const userRole = (user as any)?.role;
      const userRoles = (user as any)?.roles || [];
      const roleLower = userRole?.toLowerCase() || '';
      
      const isSuperAdmin = 
        userRole === 'SUPERADMIN' || 
        userRole === 'ADMIN' || 
        userRole === 'Super Admin' || 
        userRole === 'superadmin' ||
        userRole === 'admin' ||
        userRole === 'super_admin' ||
        roleLower === 'super admin' ||
        roleLower.includes('super admin') ||
        userRoles.some((r: any) => {
          const roleName = r.name?.toLowerCase() || '';
          const roleSlug = r.slug?.toLowerCase() || '';
          return (
            roleName.includes('super admin') || 
            roleSlug === 'super-admin' ||
            roleSlug === 'superadmin' ||
            r.name === 'Super Admin' ||
            r.name === 'SUPERADMIN' ||
            r.name === 'Admin' ||
            r.slug === 'admin'
          );
        });

      if (isSuperAdmin) {
        return true; // Super Admin can see all elements
      }

      const elements = policy?.ui?.elements;
      if (!elements) {
        // If no UI elements policy defined, default to visible (backward compatibility)
        return true;
      }
      
      // Check new categorized format
      if (typeof elements === 'object' && !Array.isArray(elements)) {
        const categorized = elements as any;
        if (categorized[category] && typeof categorized[category] === 'object') {
          // Check if element key is explicitly defined in policy
          const elementValue = categorized[category][elementKey];
          
          // If explicitly set to false, return false (hide element)
          if (elementValue === false) {
            return false;
          }
          
          // If explicitly set to true, return true (show element)
          if (elementValue === true) {
            return true;
          }
          
          // If key exists in policy object but value is undefined/null
          // This means it was explicitly set but value is missing - default to visible
          if (elementKey in categorized[category]) {
            return true;
          }
          
          // Key doesn't exist in policy - default to visible (backward compatibility)
          // Elements are visible by default unless explicitly hidden
          return true;
        }
        // Fallback to old flat format
        const flatValue = (elements as Record<string, boolean>)[elementKey];
        if (flatValue === false) {
          return false;
        }
        // Default to true if not explicitly set
        return true;
      }
      
      // Default to true if policy structure is invalid
      return true;
    };
  }, [policy, user]);

  const getWorkflowSetting = useMemo(() => {
    return (resource: string, settingKey: string): any => {
      return policy?.workflow?.[resource]?.[settingKey];
    };
  }, [policy]);

  return {
    policy,
    loading,
    error,
    can,
    scope,
    canSeeMenu,
    canSeeElement,
    getWorkflowSetting,
  };
}
